// src/components/Prescription/prescription.jsx (continued)
// FRONT OFFICE - Prescription upload component with Firebase integration

import React, { useState, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FaUpload, FaFilePdf, FaFileImage, FaTimesCircle, FaCheckCircle } from 'react-icons/fa';
import './prescription.css';

const Prescription = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const { currentUser } = useAuth();
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  const allowedFileTypes = ['image/jpeg', 'image/png', 'application/pdf'];

  // Initialize form with user data if available
  React.useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.displayName || '',
        email: currentUser.email || '',
        phone: currentUser.phoneNumber || ''
      }));
    }
  }, [currentUser]);

  // Setup drag and drop handlers
  React.useEffect(() => {
    const dropArea = dropAreaRef.current;
    if (!dropArea) return;

    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const highlight = () => {
      dropArea.classList.add('highlight');
    };

    const unhighlight = () => {
      dropArea.classList.remove('highlight');
    };

    const handleDrop = (e) => {
      preventDefaults(e);
      unhighlight();
      
      const dt = e.dataTransfer;
      const files = dt.files;
      
      if (files && files.length) {
        handleFiles(files[0]);
      }
    };

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropArea.addEventListener(eventName, preventDefaults, false);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      dropArea.addEventListener(eventName, highlight, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      dropArea.addEventListener(eventName, unhighlight, false);
    });

    dropArea.addEventListener('drop', handleDrop, false);

    // Cleanup
    return () => {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea?.removeEventListener(eventName, preventDefaults, false);
      });
      
      ['dragenter', 'dragover'].forEach(eventName => {
        dropArea?.removeEventListener(eventName, highlight, false);
      });
      
      ['dragleave', 'drop'].forEach(eventName => {
        dropArea?.removeEventListener(eventName, unhighlight, false);
      });
      
      dropArea?.removeEventListener('drop', handleDrop, false);
    };
  }, []);

  const handleFiles = (file) => {
    if (!file) return;

    // Validate file type
    if (!allowedFileTypes.includes(file.type)) {
      setUploadStatus('Error: Only JPG, PNG, or PDF files are allowed.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus('Error: File size exceeds 5MB limit.');
      return;
    }

    setSelectedFile(file);
    setUploadStatus('');

    // Create preview for images
    if (file.type.includes('image')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFiles(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setUploadStatus('Please select a file first.');
      return;
    }

    setIsLoading(true);
    setUploadStatus('Uploading...');

    try {
      // Upload file to Firebase Storage
      const storageRef = ref(storage, `prescriptions/${Date.now()}_${selectedFile.name}`);
      const snapshot = await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Save metadata to Firestore
      await addDoc(collection(db, "prescriptions"), {
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        fileURL: downloadURL,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes,
        userId: currentUser?.uid || null,
        status: 'pending',
        createdAt: Timestamp.now()
      });
      
      setUploadStatus('Upload successful! We will review your prescription and contact you soon.');
      setUploadSuccess(true);
      
      // Reset after successful upload
      setTimeout(() => {
        setSelectedFile(null);
        setPreview(null);
        setFormData({
          ...formData,
          notes: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('Upload failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploadStatus('');
    setUploadSuccess(false);
  };

  return (
    <div className="prescription-page">
      <div className="container">
        <div className="prescription-header">
          <h1>Upload Your Prescription</h1>
          <p>We accept doctor's prescriptions in JPG, PNG, or PDF format.</p>
        </div>

        <div className="prescription-content">
          <form onSubmit={handleUpload} className="prescription-form">
            <div className="prescription-form-fields">
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your phone number"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="notes">Additional Notes (Optional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions or information"
                  rows="3"
                ></textarea>
              </div>
            </div>
            
            <div className="upload-area-container">
              <label className="upload-label">Prescription File</label>
              
              {!selectedFile ? (
                <div 
                  className="upload-area" 
                  ref={dropAreaRef}
                  onClick={() => fileInputRef.current.click()}
                >
                  <FaUpload className="upload-icon" />
                  <p>Drag & drop your prescription here or click to browse</p>
                  <p className="file-requirements">JPG, PNG, or PDF (Max 5MB)</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              ) : (
                <div className="file-preview">
                  {preview ? (
                    <img src={preview} alt="Prescription preview" className="preview-image" />
                  ) : (
                    <div className="file-info">
                      {selectedFile.type === 'application/pdf' ? (
                        <FaFilePdf className="file-icon pdf" />
                      ) : (
                        <FaFileImage className="file-icon image" />
                      )}
                      <p className="file-name">{selectedFile.name}</p>
                      <p className="file-size">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                  )}
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    <FaTimesCircle /> Remove File
                  </button>
                </div>
              )}
            </div>
            
            {uploadStatus && (
              <div className={`status-message ${uploadStatus.includes('Error') ? 'error' : (uploadSuccess ? 'success' : '')}`}>
                {uploadSuccess ? <FaCheckCircle className="status-icon" /> : null}
                {uploadStatus}
              </div>
            )}
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={isLoading || !selectedFile}
              >
                {isLoading ? 'Uploading...' : 'Upload Prescription'}
              </button>
            </div>
          </form>
          
          <div className="prescription-info">
            <div className="info-card">
              <h3>Why Upload Your Prescription?</h3>
              <p>Uploading your prescription allows our pharmacists to prepare your medication before you arrive, saving you time and ensuring accuracy.</p>
            </div>
            
            <div className="info-card">
              <h3>What Happens Next?</h3>
              <p>After uploading, our team will review your prescription and contact you to confirm your order and arrange payment and delivery options.</p>
            </div>
            
            <div className="info-card">
              <h3>Privacy & Security</h3>
              <p>Your prescription information is encrypted and secure. We comply with all healthcare privacy regulations to protect your medical information.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prescription;












// import React, { useState } from 'react';
// import './prescription.css';

// const PrescriptionUpload = () => {
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [preview, setPreview] = useState(null);
//   const [uploadStatus, setUploadStatus] = useState('');
//   const [isLoading, setIsLoading] = useState(false);

//   const allowedFileTypes = ['image/jpeg', 'image/png', 'application/pdf'];

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
    
//     if (!file) return;

//     // Validate file type
//     if (!allowedFileTypes.includes(file.type)) {
//       setUploadStatus('Error: Only JPG, PNG, or PDF files are allowed.');
//       return;
//     }

//     // Validate file size (5MB max)
//     if (file.size > 5 * 1024 * 1024) {
//       setUploadStatus('Error: File size exceeds 5MB limit.');
//       return;
//     }

//     setSelectedFile(file);
//     setUploadStatus('');

//     // Create preview for images
//     if (file.type.includes('image')) {
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setPreview(reader.result);
//       };
//       reader.readAsDataURL(file);
//     } else {
//       setPreview(null);
//     }
//   };

//   const handleUpload = async () => {
//     if (!selectedFile) {
//       setUploadStatus('Please select a file first.');
//       return;
//     }

//     setIsLoading(true);
//     setUploadStatus('Uploading...');

//     // Simulate API upload (replace with actual API call)
//     try {
//       // In a real app, you would use fetch or axios to upload to your server
//       await new Promise(resolve => setTimeout(resolve, 1500));
      
//       setUploadStatus('Upload successful!');
//       // Reset after successful upload
//       setTimeout(() => {
//         setSelectedFile(null);
//         setPreview(null);
//         setUploadStatus('');
//       }, 2000);
//     } catch (error) {
//       setUploadStatus('Upload failed. Please try again.');
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleCancel = () => {
//     setSelectedFile(null);
//     setPreview(null);
//     setUploadStatus('');
//   };

//   return (
//     <div className="prescription-upload">
//       <h2>Upload Prescription</h2>
      
//       <div className="upload-area">
//         {!selectedFile ? (
//           <div className="file-selector">
//             <input
//               type="file"
//               id="prescription-upload"
//               accept=".jpg,.jpeg,.png,.pdf"
//               onChange={handleFileChange}
//               style={{ display: 'none' }}
//             />
//             <label htmlFor="prescription-upload" className="upload-button">
//               Choose File
//             </label>
//             <p>or drag and drop file here</p>
//             <p className="file-requirements">JPG, PNG, or PDF (Max 5MB)</p>
//           </div>
//         ) : (
//           <div className="file-preview">
//             {preview ? (
//               <img src={preview} alt="Prescription preview" className="preview-image" />
//             ) : (
//               <div className="file-info">
//                 <p>{selectedFile.name}</p>
//                 <p>{(selectedFile.size / 1024).toFixed(2)} KB</p>
//               </div>
//             )}
//             <div className="upload-actions">
//               <button onClick={handleCancel} className="cancel-button">
//                 Cancel
//               </button>
//               <button 
//                 onClick={handleUpload} 
//                 className="upload-button"
//                 disabled={isLoading}
//               >
//                 {isLoading ? 'Uploading...' : 'Upload Prescription'}
//               </button>
//             </div>
//           </div>
//         )}
//       </div>
      
//       {uploadStatus && (
//         <p className={`status-message ${uploadStatus.includes('Error') ? 'error' : 'success'}`}>
//           {uploadStatus}
//         </p>
//       )}
//     </div>
//   );
// };

// export default PrescriptionUpload;